import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { format } from "lib/globalize-util";
import { default as ReactImageMagnify } from "react-image-magnify";
import ZoomIcon from "@material-ui/icons/ZoomIn";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import makeStyles from "@material-ui/core/styles/makeStyles";
import type { IScope } from "reducers/scope";
import ImageSafe from "components/image";
import Banner from "components/items/item/banner";
import ColorBanner from "components/items/item/color-banner";
import { IItemRecord, ImageSize } from "records/item/item";
import { IMedia360, IMediaType, IMediaVideo, IProductMedia, IMedia3D } from "constants/product-media";
import { MediaLightBox, PANEL_SIZE } from "components/product-media/media-lightbox";
import Image360 from "components/product-media/image-360";
import { useGetSafeImages } from "helpers/product-media-helpers";
import { IVideoRef, VideoPlayer } from "components/product-media/video-player";
import me from "models/me";
import theme from "styles/theme";
import { MEDIA_TYPE_360, MEDIA_TYPE_IMAGE, MEDIA_TYPE_VIDEO, MEDIA_TYPE_THREEDIUM, isTablet } from "constants/media";
import ManifestLoader from "components/product-media/components/manifest-loader";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import classnames from "classnames";
import { DATATEST } from "constants/integration";
import { blurFix } from "styles/mixins";
import { IStore } from "reducers";
import { connect } from "react-redux";

import { Async3DPlayerWrapper } from "./player-3d-async";
import { ContentLoading } from "components/content-loading";
import WatermarkProvider from "components/product-media/watermark-provider";
import { IWatermarking } from "reducers/watermarking";
// prettier-ignore
/** Default media content width */
const WIDTH = 245;
/** Default media content height */
const HEIGHT = 386;
// reduced high resolution image size to decrease processing time for watermarking
const WATERMARKING_SIZE = "W";

interface IProps {
    product: IItemRecord;
    selectedIndex: number;
    onChangeMediaIndex?: (index: number) => void;
    width?: string | number;
    height?: string | number;
    /** Show a preview for all media, otherwise for images only. Default to true */
    useMediaPreview?: boolean;
    /** Use ReactMagnify to show zoom preview to the right from the main image. True by default */
    useMagnifyZoom?: boolean;
    /** Show lightbox in full screen, otherwise reserve place for left panel. Default to true */
    fullWidthLightbox?: boolean;
    /** Show Inactive banners on the image. True by default */
    showDisabledBanner?: boolean;
    useCarousel?: boolean;
    mediaSize?: ImageSize;
}

interface IStateProps {
    touchDevice: boolean;
    viewport: string;
    scope: IScope;
    watermarking: IWatermarking;
}

const JumbotronComponent: FC<IProps & IStateProps> = (props) => {
    const {
        product,
        scope,
        width = WIDTH,
        height = HEIGHT,
        useMediaPreview = true,
        useCarousel = false,
        mediaSize = "M",
    } = props;
    const { selectedIndex, onChangeMediaIndex } = props;
    const { showDisabledBanner = true, useMagnifyZoom = true, fullWidthLightbox = true } = props;
    const { touchDevice, viewport, watermarking } = props;

    const prevProduct = useRef<IItemRecord>();

    const [lightBoxOpen, setLightBoxOpen] = useState<boolean>(false);
    const [highResImage, setHighResImage] = useState<{ src: string; height: number; width: number }>(null);

    const [isFetchingMedia, setIsFetchingMedia] = useState<boolean>(null);
    const [isFetchingHighResMedia, setIsFetchingHighResMedia] = useState<boolean>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const media = useMemo<IProductMedia[]>(() => product.getMedia(scope, mediaSize), [product]); // optimization not to generate list of media
    // prettier-ignore
    const currentIndex = useMemo<number>(
        () => (useMediaPreview ? selectedIndex : _(media).slice(0, selectedIndex + 1).findLastIndex({ type: MEDIA_TYPE_IMAGE })),
        [useMediaPreview, media, selectedIndex],
    );
    const current: IProductMedia = useMemo<IProductMedia>(() => media[currentIndex], [media, currentIndex]);

    const player = useRef<IVideoRef>(null);

    const useImage = !useMagnifyZoom || !current || window.navigator.userAgent.indexOf("Edge") > -1;

    const disable3DRendering = useMemo(
        () => touchDevice && isTablet(viewport) && scope?.brand?.hasPreference("disable_mobile_browser_3d"),
        [touchDevice, viewport, scope],
    );

    const isType = useCallback(
        (type: IMediaType) => (current ? current.type === type : type === MEDIA_TYPE_IMAGE),
        [current],
    ); // with fallback to image in case of bad data
    const handleLightBox = useCallback(
        (open: boolean) => (event?: React.SyntheticEvent<unknown>) => {
            event?.stopPropagation();
            setLightBoxOpen(open);
            // prevent opening media in lightbox if useMediaPreview is false
            if (selectedIndex !== currentIndex) {
                onChangeMediaIndex(currentIndex);
            }
            if (open && player && player.current) {
                player.current.pause();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [player, selectedIndex, currentIndex],
    );
    const handleOpenLightbox = useMemo(() => handleLightBox(true), [handleLightBox]);
    const getSafeImages = useGetSafeImages(scope);

    const watermarkingEnabled = useMemo(
        () => scope.brand.hasPreference("add_watermarking") && watermarking?.active,
        [scope.brand, watermarking?.active],
    );

    const mediaUrls = useMemo<string[]>(
        () => (useImage ? getSafeImages(current) : highResImage ? [current.source] : []),
        [current, getSafeImages, highResImage, useImage],
    );
    const fullResMedia = useMemo(
        () => product?.getMedia(scope, watermarkingEnabled ? WATERMARKING_SIZE : null),
        [product, scope, watermarkingEnabled],
    );

    useEffect(() => {
        const image = product.getMedia(scope, watermarkingEnabled ? WATERMARKING_SIZE : null)[currentIndex];

        if (useImage || !image || image.type !== MEDIA_TYPE_IMAGE) {
            setHighResImage(null);
            return; // do not need to preload the selected image for magnify zoom
        }

        const img = new Image();
        img.onload = () => {
            const multiplier = img.width <= 300 || img.height <= 400 ? 5 : 1;
            setHighResImage({
                src: image.source,
                height: img.height * multiplier,
                width: img.width * multiplier,
            });
        };
        img.src = image.source;
        return () => (img.src = ""); // cancel loading
    }, [product, currentIndex, useImage, useMediaPreview]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (prevProduct.current && prevProduct.current?._id != product?._id) {
            // reset state for Product Panel
            setIsFetchingMedia(null);
            setIsFetchingHighResMedia(null);
        }
        prevProduct.current = product;
    }, [product]);

    const disableText = getDisabledText(product, scope);
    const classes = useStyles({ width, height, isCarousel: useCarousel });

    const magnifyStyles = useMemo(
        () => ({
            className: "jumbotronImageContainer",
            imageClassName: "jumbotronSmallImage",
            style: {
                cursor: "zoom-in",
            },
            enlargedImageStyle: {
                zIndex: 100,
            },
        }),
        [],
    );

    const handleNextMedia = useCallback(() => {
        if (selectedIndex < _.size(media) - 1) {
            onChangeMediaIndex(selectedIndex + 1);
        }
    }, [onChangeMediaIndex, selectedIndex, media]);
    const handlePrevMedia = useCallback(() => {
        if (selectedIndex > 0) {
            onChangeMediaIndex(selectedIndex - 1);
        }
    }, [onChangeMediaIndex, selectedIndex]);

    return (
        <div className={classes.root}>
            <div className={classes.media}>
                {useCarousel && (
                    <ChevronLeftIcon
                        className={classnames(classes.carouselIcon, classes.prevIcon, {
                            disabled: selectedIndex === 0,
                        })}
                        onClick={handlePrevMedia}
                        data-test={DATATEST.PDP.PREV_IMAGE_BUTTON}
                    />
                )}

                {isType(MEDIA_TYPE_IMAGE) && isFetchingMedia && <ContentLoading className={classes.loading} />}
                {isType(MEDIA_TYPE_IMAGE) && !isFetchingMedia && (
                    <div
                        data-test={DATATEST.GALLERY.IMAGE_MAGNIFY_CONTAINER}
                        className={classes.image}
                        onClick={handleOpenLightbox}
                    >
                        {!useImage && highResImage && (
                            <WatermarkProvider imageUrl={current.source}>
                                {(src, isLoading) =>
                                    isLoading || !src ? (
                                        <ContentLoading className={classes.loading} />
                                    ) : (
                                        <ReactImageMagnify
                                            smallImage={{
                                                alt: product.name,
                                                src,
                                                height,
                                                width,
                                            }}
                                            largeImage={highResImage}
                                            isHintEnabled={false}
                                            fadeDurationInMs={150}
                                            hoverDelayInMs={150}
                                            {...magnifyStyles}
                                        />
                                    )
                                }
                            </WatermarkProvider>
                        )}
                        {useImage && (
                            <ImageSafe
                                alt={product.name}
                                src={mediaUrls}
                                style={{ height, width }}
                                showWatermark={true}
                            />
                        )}
                        <Banner item={product} scope={scope} />
                        {product && product.hasCustomizationBanner(scope) && (
                            <Banner item={product} banner={product.getCustomizationBanner(scope)} scope={scope} />
                        )}
                        <ColorBanner item={product} />
                        {showDisabledBanner && disableText && <div className={classes.banner}>{disableText}</div>}
                    </div>
                )}
                {isType(MEDIA_TYPE_360) && (
                    <ManifestLoader manifest={(current as IMedia360).manifest} src={current.source}>
                        {(source, preloading) => (
                            <Image360
                                width={width}
                                height={height}
                                source={source}
                                preloading={preloading}
                                hint={true}
                                data-test={DATATEST.PDP.JUMBO_IMAGE_360}
                            />
                        )}
                    </ManifestLoader>
                )}
                {isType(MEDIA_TYPE_VIDEO) && (
                    <VideoPlayer
                        ref={player}
                        source={(current as IMediaVideo).source}
                        poster={(current as IMediaVideo).poster}
                        lightMode={true}
                    />
                )}
                {/*
                 * If lightbox is open we should destroy 3d-player in jumbotron.
                 * Multiple open threedium iframes will lead to crush in mobile.
                 */}

                {isType(MEDIA_TYPE_THREEDIUM) && !lightBoxOpen && (
                    <Async3DPlayerWrapper
                        useFitToView={true}
                        threediumId={(current as IMedia3D).threedium_id}
                        disableRendering={disable3DRendering}
                        onClick={handleLightBox as any}
                    />
                )}
                {useCarousel && (
                    <ChevronRightIcon
                        className={classnames(classes.carouselIcon, classes.nextIcon, {
                            disabled: selectedIndex === _.size(media) - 1,
                        })}
                        onClick={handleNextMedia}
                        data-test={DATATEST.PDP.NEXT_IMAGE_BUTTON}
                    />
                )}
            </div>
            <div className={classes.tip}>
                {isType(MEDIA_TYPE_IMAGE) && format("Click image to zoom")}
                {!isType(MEDIA_TYPE_IMAGE) && (
                    <Tooltip
                        title={<span className={classes.tooltip}>{format("Click to zoom")}</span>}
                        placement="top-end"
                    >
                        <IconButton
                            className={classes.zoomButton}
                            onClick={handleOpenLightbox}
                            data-test={DATATEST.PDP.VIEW_IN_LIGHTBOX_BUTTON}
                        >
                            <ZoomIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            {lightBoxOpen && ( // optimization not to load media
                <MediaLightBox
                    open={lightBoxOpen}
                    dataLoading={isFetchingHighResMedia}
                    media={fullResMedia}
                    currentIndex={selectedIndex}
                    onClose={handleLightBox(false)}
                    onViewChange={onChangeMediaIndex}
                    panelWidth={fullWidthLightbox ? 0 : PANEL_SIZE}
                />
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme, { width?: string | number; height?: string | number; isCarousel: boolean }>({
    root: {
        flexGrow: 0,
        flexShrink: 0,
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
    },
    media: ({ width = WIDTH, height = HEIGHT, isCarousel }) => ({
        width,
        height,
        marginLeft: isCarousel ? 30 : 0,
        marginRight: isCarousel ? 30 : 0,
        cursor: "pointer",
        position: "relative",
    }),
    carouselIcon: {
        color: theme.palette.secondaryTextColor,
        fontSize: 24,
        position: "absolute",
        top: 0,
        bottom: 0,
        marginTop: "auto",
        marginBottom: "auto",
        zIndex: -1,
        "&.disabled": {
            color: theme.palette.lightGrey,
            cursor: "initial",
        },
    },
    prevIcon: {
        left: -30,
    },
    nextIcon: {
        right: -30,
    },
    image: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& img": {
            objectFit: "contain",
            ...blurFix,
        },
    },
    banner: {
        position: "absolute",
        top: "50%",
        left: 0,
        right: 0,
        zIndex: 4,
        backgroundColor: "rgba(45, 45, 45, .8)",
        lineHeight: "50px",
        transform: "translateY(-50%)",
        color: "white",
        textAlign: "center",
        fontSize: 16,
        fontWeight: "normal",
        pointerEvents: "none",
    },
    loading: {
        background: "transparent",
    },
    tip: {
        marginTop: 12,
        minHeight: 14,
        fontSize: 10,
        textAlign: "center",
        position: "relative",
    },
    zoomButton: {
        color: theme.palette.darkerGrey,
        position: "absolute",
        top: -7,
        right: 10,
        zIndex: 1000,
        transform: "translateY(-50%)",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.7)",
        "&:hover": {
            backgroundColor: "rgba(245,245,245,0.85)",
        },
    },
    tooltip: {
        fontSize: 12,
        lineHeight: "18px",
    },
});

function getDisabledText(product: IItemRecord, scope: IScope) {
    const availableForOtherCurrencies =
        product._disabled && _.some(scope.availableCurrencies, (code) => product.__pricing.get(code as any));

    if (!product.isAvailable(scope)) {
        return format("Inactive");
    } else if (availableForOtherCurrencies && !scope.brand.hasPreference("hide_currency_warning")) {
        return format("Not available for |code|", { code: scope.currencyCode });
    } else if (scope.viewAs) {
        if (product._disabled) {
            return format("Invisible to company");
        } else if (
            !me.isGuestBuyer() &&
            !scope.brand.default_permissions.buyer.includes("view_all_warehouses") &&
            product.__warehouse_ids.length
        ) {
            if (
                !scope.viewAs.__settings.__warehouses.some((warehouse) => product.__warehouse_ids.includes(warehouse))
            ) {
                return format("Invisible to company");
            }
        }
    }
    return null;
}

const mapStateToProps = (store: IStore): IStateProps => ({
    viewport: store.app.viewport,
    touchDevice: store.app.touchDevice,
    scope: store.scope,
    watermarking: store.watermarking?.payload,
});

export const Jumbotron = connect<IStateProps, null, IProps>(mapStateToProps)(JumbotronComponent);
export default Jumbotron;
